import React from 'react';
import robot from '../../Images/varchat-icon-robot-2.svg';
import robotDark from '../../Images/varchat-icon-robot-dark.svg';
import user from '../../Images/varchat-icon-user-2.svg';
import userDark from '../../Images/varchat-icon-user-dark.svg';
import { forwardRef, useState, useEffect } from 'react';
import Rating from './Rating/Rating';
import { useDispatch, useSelector } from 'react-redux';
import { selectMessages, updateMessage } from '../../slice/MessageSlice';
import WelcomeMessage from './WelcomeMessage';
import examples from '../../constants/example';
import { isMobile } from 'react-device-detect';
import CopyIcon from './CopyIcon';
import { selectLanguage, selectTheme } from '../../slice/SettingSlice';
import MessageFlagIcons from './MessageFlagIcons';
import { setShowHelpCenter, setShowLogin, setShowProfile } from '../../slice/ModalVisibilitySlice';
import Loader from './Loader/Loader';
import { selectBearer } from '../../slice/bearerSlice';
import { selectStreaming, selectTranslating } from '../../slice/loaderSlice';
import { selectActiveConversationUuid, setTriggerTopicFocus, selectTriggerTopicFocus, selectFailedFetchQuestionsConversationUuid, selectActiveConversationHasChatNotEmpty } from "../../slice/ConversationSlice";
import ReadMore from './ReadMore';
import WarningIcon from '../../Images/WarningIcon';
import ErrorIcon from '../../Images/ErrorIcon';
import ExampleList from './ExampleList';
import arrow from '../../Images/arrow.svg';
import TooltipStreamRule from './TooltipStreamRule';
import acmgRulesDescription from '../../constants/acmgRulesDescription';
import { splitMessageTextByReferences } from '../../utils/messageReferences.js';

import { Url } from '../../constants/global.js';

const Message = forwardRef(function Message(props, ref) {
    const dispatch = useDispatch();
    const [expand, setExpand] = useState(false);
    const [expandRef, setExpandRef] = useState(false);
    const messages = useSelector(selectMessages);
    const message = messages[props.ind];
    const previousMessage = props.ind > 0 ? messages[props.ind - 1] : null;
    const theme = useSelector(selectTheme);
    const robotIcon = theme === 'light' ? robot : robotDark;
    const userIcon = theme === 'light' ? user : userDark;
    const language = useSelector(selectLanguage);
    const logToken = useSelector(selectBearer);
    const translating = useSelector(selectTranslating);
    const streaming = useSelector(selectStreaming);
    const activeConversationUuid = useSelector(selectActiveConversationUuid);
    const isActiveTopicAlreadyVisible = useSelector(selectTriggerTopicFocus);
    const activeConversationHasChatNotEmpty = useSelector(selectActiveConversationHasChatNotEmpty);
    const failedFetchQuestionsConversationUuid = useSelector(selectFailedFetchQuestionsConversationUuid);
    let messageBody = '';
    let messageToCopy = '';
    const statusInfoMessage = 'We cannot retrieve all the information at this moment, so the answer may be partial or incomplete. If you\'re not satisfied with this summary, please try again later.';
    const errMessage = <span>VarChat is unable to complete this request right now because of an internal error. Please, try again.<br />If the problem persists, please contact <a className='link-error' href='mailto:varchat@engenome.com'>varchat@engenome.com</a></span>;
    const helpCenterLink = <div className='mt-2'>For more details on the supported input formats, please visit our <span className='message-link' onClick={() => { dispatch(setShowHelpCenter(true)) }}>Help Center</span></div>
    const unsupportedInvFeedback = message.statusCode === 12 && typeof message.feedback === 'string' && message.feedback.includes('Currently INV type variants are not supported.') ? message.feedback.trim() : '';
    const status12Text = 'Please check the information you provided and verify the HGVS nomenclature' +
        (unsupportedInvFeedback ? ', ' + unsupportedInvFeedback.charAt(0).toLowerCase() + unsupportedInvFeedback.slice(1) : '.');

    const statusCodeLegend = [
        { statusCode: -1, type: 'errorValidation', text: '' }, //-1 case fe validation fail, i messaggi sono gestiti in SendMessageButton in base all'errore di validazione
        { statusCode: 0, type: (message.statusInfoCode ? 'warning' : 'ok'), text: '' }, //type warning serve per inserire l'icona gialla quando è 0 ma Status_info_code è true
        { statusCode: 1, type: 'warning', text: 'VarChat identified supporting literature, but it was not used to generate the summary.' },
        { statusCode: 2, type: 'warning', text: 'VarChat did not find any supporting literature.' },
        { statusCode: 3, type: 'error', text: 'We\'re currently encountering issues and can\'t provide a summary right now. Please try again later.' },
        { statusCode: 4, type: 'error', text: (message.statusInfoCode ? errMessage : 'VarChat is unable to retrieve information on this variant. Please check the information you provided, or try another VarChat-supported format (HGVS or genomic coordinates notation).') },
        { statusCode: 5, type: 'error', text: errMessage },
        { statusCode: 6, type: 'error', text: (message.statusInfoCode ? errMessage : 'These genomic coordinates seem to be incorrect. Please check the information you provided and verify the genomic assembly. Alternatively, try another VarChat-supported format (HGVS or rs notation).') },
        { statusCode: 7, type: 'error', text: (message.statusInfoCode ? errMessage : 'Please check the gene symbol since it’s not valid.') },
        { statusCode: 8, type: 'error', text: (message.statusInfoCode ? errMessage : 'Please check the transcript ID you provided or try to query by gene.') },
        { statusCode: 9, type: 'error', text: errMessage },
        { statusCode: 10, type: 'warning', text: 'VarChat is unable to find the answer to this question in the identified literature.' },
        { statusCode: 11, type: 'warning', text: 'The queried dbSNP id is only mentioned in Supplementary Information, therefore we cannot provide a summary.' },
        { statusCode: 12, type: 'error', text: status12Text },
        { statusCode: 100, type: 'warning', text: 'It seems you reached your daily usage limit, if you want to continue using VarChat please Log In' }
    ];

    const getStatusConfig = (statusCode) => {
        const normalizedStatusCode = /^\-1(\.\d)?$/.test(statusCode) ? -1 : statusCode;
        return statusCodeLegend.find((status) => status.statusCode === normalizedStatusCode)
            ?? { type: 'error', text: errMessage };
    };

    //metto il messaggio dell'errore di traduzione perché non si può salvare in uno stato un html
    const translationError = <span className='error'><ErrorIcon /><span>I was unable to complete the translation right now. Please try again later. <br />If the problem persists, please contact <a className='link-error' href='mailto:varchat@engenome.com'>varchat@engenome.com</a></span></span>;
    const translation = (message.translation && message.translation[message.language] !== undefined ? (message.translation[message.language] === 'error' ? (translating ? <Loader loading_message={'TRANSLATING...'} /> : translationError) : message.translation[message.language]) : <Loader loading_message={'TRANSLATING...'} />); //in message.translation mi salvo le n traduzioni nelle varie lingue

    let messageText = (message.language && message.language !== 'EN' ? translation : message.text);

    useEffect(() => { //to set time only once
        if (!message.time) {
            dispatch(updateMessage({ i: props.ind, obj: { time: getFormattedTime() } }))
        }
    }, [message.time]);

    function isGeneOnlyInputSearch() {
        if (message.userInput) {
            return message.userInput.gene != ""
                && (message.userInput.rs == ""
                    && message.userInput.hgvs_c == ""
                    && message.userInput.hgvs_p == ""
                    && message.userInput.hgvs_m == ""
                    && message.userInput.genomic_coord == ""
                );
        } else {
            return false;
        }
    }

    function getFormattedTime() {
        const date = new Date();
        const timeComponents = [date.getHours(), date.getMinutes()];
        return timeComponents
            .map(component => {
                const pad = (component < 10) ? '0' : '';
                return pad + component;
            })
            .join(':');
    }

    function createRefTable() {
        return <div className={expandRef ? "section-exp" : 'section-compact'}><table><tbody>{message.cits.map((ref, j) => {
            const suppInd = ref.cit.indexOf(' [SUPP]');
            return <tr key={j}><td className='ref-col'><a href={ref.pmid} target='_blank' rel="noreferrer" className='link-generic'>{suppInd !== -1 ? ref.cit.substring(0, suppInd) : ref.cit}</a><i>{suppInd !== -1 ? ' Variant reported in Supplementary Information' : ''}</i></td></tr>
        })}</tbody></table>
        </div>
    }

    function extractRcvByMatch(match_rs) {
        const l = message.rcvCits.filter((rcvObj) => {
            return rcvObj.match_by_rs == match_rs;
        });
        return l.sort((a, b) => a.weight > b.weight ? -1 : 1);
    }

    function createRcvTableRow(ref, j) {
        return <>
            <tr key={j} name={ref.clinv_table.length === 1 ? 'row-with-border' : 'row-no-border'}>
                <td className='rcv-ref-col' rowSpan={ref.clinv_table.length} key={'row ' + j + 'col ' + 0}><a href={ref.link} target='_blank' rel="noreferrer" className='link-generic'>{ref.rcv}</a></td>
                <td className='rcv-ref-col' rowSpan={ref.clinv_table.length} key={'row ' + j + 'col ' + 1}>{ref.condition}</td>
                <td className='rcv-ref-col' key={'row ' + j + 'col ' + 2}>{ref.clinv_table[0].clin_sig}</td>
                <td className='rcv-ref-col' style={{ whiteSpace: 'nowrap', display: 'flex' }} key={'row ' + j + 'col ' + 3}>{getStars(ref.clinv_table[0].rev_status)}</td>
            </tr>
            {ref.clinv_table.length === 1 ? null : ref.clinv_table.map((ref_el, i) => {
                if (i !== 0) {
                    return <tr key={'row ' + j + 'sub ' + i} name={ref.clinv_table.length - 1 === i && message.rcvCits.length - 1 !== j ? 'row-with-border' : 'row-no-border'}>
                        <td className='rcv-ref-col' key={'row ' + j + 'sub ' + i + 'col ' + 0}>{ref_el.clin_sig}</td>
                        <td className='rcv-ref-col' style={{ whiteSpace: 'nowrap' }} key={'row ' + j + 'sub ' + i + 'col ' + 1}>{getStars(ref_el.rev_status)}</td>
                    </tr>
                } else {
                    return <></>
                }
            })}
        </>
    }

    function createRcvRefTable() {
        const matchRsZeroRcv = extractRcvByMatch(0);
        const matchRsOneRcv = extractRcvByMatch(1);

        return <div className={expand ? "section-exp" : 'section-compact'} style={{ overflowX: 'auto' }}>
            <table name='clinvar-table'>
                <tbody>
                    {matchRsZeroRcv.length > 0
                        ? matchRsZeroRcv.map((ref, j) => { // message.rcvCits
                            return <React.Fragment key={j}>
                                {j === 0 ? <tr key={Date.now()} name={'row-with-black-border'}><td colSpan={4}><p style={{ fontSize: '16px', marginBottom: '10px' }}><b>Exact variant match</b></p></td></tr> : <></>}
                                {createRcvTableRow(ref, j)}
                            </React.Fragment>
                        })
                        : (<><tr key={Date.now()} name={'row-with-black-border'}><td colSpan={4}><p style={{ fontSize: '16px', marginBottom: '10px' }}><b>Exact variant match</b></p></td></tr>
                            <tr key={Date.now() + 1}><td name={'cnv-table-no-data'} style={{ paddingTop: '10px' }} colSpan={4}><p><i>No RCV available</i></p></td></tr>
                        </>)
                    }
                    {matchRsOneRcv.length === 0 ? <></> : <tr><td colSpan={4} style={{ height: '50px' }}></td></tr>}
                    {matchRsOneRcv.length > 0
                        ? matchRsOneRcv.map((ref, j) => {
                            return <React.Fragment key={j}>
                                {j === 0 ? <tr key={Date.now()} name={'row-with-black-border'}><td colSpan={4}><p style={{ fontSize: '16px', marginBottom: '10px' }}><b>Similar variants</b></p></td></tr> : <></>}
                                {createRcvTableRow(ref, j)}
                            </React.Fragment>
                        })
                        : (<></>)
                    }
                </tbody>
            </table>
        </div>;
    }

    function getStars(count) {
        let stars = [];
        for (let i = 0; i < count; i++) {
            stars.push(<span key={'star ' + i} className='rating-icon full clinvar-stars-check' />)
        }
        return stars;
    }

    function clickNeedTranslation() {
        if (logToken !== '') {
            dispatch(setShowProfile({ show: true, type: 'translationButton' }));
        } else {
            dispatch(setShowLogin({ show: true, type: 'settingButton' }));
        }
    }

    function clickedUnloggedUserLoginLink(e) {
        e.preventDefault();
        dispatch(setShowLogin({ show: true, type: '' }));
    }

    //function to extract the text of an html react element (if the passed object is already a string returns it)
    function textContent(elem) {
        if (!elem) {
            return '';
        }
        if (typeof elem === 'string') {
            return elem;
        }
        const children = elem.props && elem.props.children;
        if (children instanceof Array) {
            return children.map(textContent).join('');
        }
        return textContent(children);
    }

    function isValidInitialSearchMessage() {
        /*console.log(message.type == null || message.type === 'answer',
        message.user === 'VarChat',
        message.statusCode == 0,
        message.conversationUuid === activeConversationUuid, 
        !isActiveTopicAlreadyVisible) */
        return message.type == null
            && message.user === 'VarChat'
            && message.statusCode == 0
            && message.conversationUuid === activeConversationUuid
            && !isActiveTopicAlreadyVisible;
    }

    function hasntDiscoverMoreConditions() {
        //    console.log(message.text.substring(0, 50), "      ", message.conversationUuid, " | ", activeConversationUuid, " | ", failedFetchQuestionsConversationUuid)
        return logToken == ""
            ? (message.statusCode != 0
                || message.conversationUuid !== activeConversationUuid
                || message.type
                || message.user !== 'VarChat'
                || getStatusConfig(message.statusCode).type === 'error'
            )
            : (message.statusCode != 0
                || message.conversationUuid === failedFetchQuestionsConversationUuid
                || message.conversationUuid !== activeConversationUuid
                || message.type
                || message.user !== 'VarChat'
                || getStatusConfig(message.statusCode).type === 'error'
                || !activeConversationHasChatNotEmpty
            )
    }

    function replaceTextWithReferenceLinks(text) {
        if (typeof text !== 'string') {
            return text;
        }

        return splitMessageTextByReferences(text, message.cits).map((part, index) => {
            return typeof part === 'string'
                ? part
                : <a
                    key={`reference-${part.referenceNumber}-${index}`}
                    href={part.href}
                    target='_blank'
                    rel='noreferrer'
                    className='link-generic'
                    style={{ textDecoration: 'underline' }}
                    title={part.title || `Reference ${part.referenceNumber}`}
                    aria-label={`Open reference ${part.referenceNumber}`}
                >
                    {part.label}
                </a>
        });
    }

    function replaceTextWithTitleElement(text) {
        if (typeof text !== 'string') {
            return text;
        }

        const title = "Variant's annotation and classification";
        return text.includes(title)
            ? <>{replaceTextWithReferenceLinks(text.split(title)[0])}
                <b>{title}</b>
                {replaceTextWithReferenceLinks(text.split(title)[1])}
            </>
            : replaceTextWithReferenceLinks(text)
    }

    function addRulesInfoPointToSearchMessage(messageText) {
        if (!streaming && message.user === 'VarChat' && !message.type && messageText && (typeof messageText === 'string')) {
            let rulesInText = [];
            Object.keys(acmgRulesDescription).forEach((ruleName => {
                if (messageText.includes(ruleName)) {
                    rulesInText.push(ruleName)
                }
            }));

            rulesInText.forEach(ruleName => {
                if (messageText.includes(ruleName)) {
                    // ruleName non contenuto in una parola
                    const r = new RegExp(`\\b${ruleName}\\b`, 'g');
                    messageText = messageText.replace(r, (ruleName + '<split_here>'));
                }
            })

            const splittedText = messageText.split('<split_here>');
            return <>
                {
                    splittedText.map(splittedTextElement => {
                        let matchedRule = acmgRulesDescription[splittedTextElement.split(" ").pop().slice(-3)]
                            ? splittedTextElement.split(" ").pop().slice(-3)
                            : (
                                acmgRulesDescription[splittedTextElement.split(" ").pop().slice(-4)]
                                    ? splittedTextElement.split(" ").pop().slice(-4)
                                    : null
                            )

                        return <>
                            <i style={{ fontStyle: "initial" }}>{replaceTextWithTitleElement(splittedTextElement)}</i>
                            {matchedRule
                                ? <TooltipStreamRule txt={acmgRulesDescription[matchedRule]} />
                                : ""
                            }
                        </>
                    })
                }
            </>
        } else {
            return replaceTextWithTitleElement(messageText);
        }
    }

    function onAskMoreClick() {
        dispatch(setTriggerTopicFocus(true));
    }

    if (message.statusCode !== undefined) {
        let messageStatus = message.statusCode;
        if (/^\-1(\.\d)?$/.test(message.statusCode)) {
            messageStatus = -1;
        }

        const statusConfig = getStatusConfig(messageStatus);

        let statusCodeText = statusConfig.text;
        if (message.statusInfoCode && statusConfig.type !== 'error') {
            statusCodeText = statusInfoMessage;
        }

        if (message.statusCode == 7) {
            if (!isGeneOnlyInputSearch()) messageStatus = '7vnt';
        }

        messageBody = <>
            <p className={message.warningText? "warning" : statusConfig.type}>
                {statusConfig.type === 'warning' || message.warningText ? <WarningIcon /> : statusConfig.type === 'error' ? <ErrorIcon /> : null}
                {message.warningText}
                {statusCodeText}
            </p>
            {!message.statusInfoCode && examples['status_' + messageStatus] !== undefined ? <><ExampleList type={'status_' + messageStatus} />{helpCenterLink}</> : null}
            {streaming ? messageText : addRulesInfoPointToSearchMessage(messageText)}
            {messageStatus === -1 ? <>{message.statusCode !== -1 ? <div className='mt-2'><ExampleList type={'status_' + message.statusCode.toString().split('.')[1]} /></div> : null}{helpCenterLink}</> : null}
            {message.stopText !== undefined ? <div style={{ color: 'var(--soft-red)' }}>{message.stopText}</div> : ''}
        </>
        let title = ''
        try {
            title = '\n' + examples['status_' + messageStatus].title;
        } catch {
        }
        messageToCopy = (message.warningText ? message.warningText + '\n' : '') + textContent(statusCodeText) + (!message.statusInfoCode && examples['status_' + messageStatus] !== undefined ? '\n' + examples['status_' + messageStatus].text + title + '\n' + examples['status_' + messageStatus].exArray.map((str) => { return str.ex }).join('\n') + '\nNote: ' + examples['status_' + messageStatus].note + '\n' + textContent(helpCenterLink) + '\n' : '') + (messageText !== undefined ? messageText : '');
        if (messageStatus === -1 && message.statusCode !== -1) {
            try {
                title = '\n' + examples['status_' + message.statusCode.toString().split('.')[1]].title;
            } catch { }
            messageToCopy = messageToCopy.concat('\n' + examples['status_' + message.statusCode.toString().split('.')[1]].text + title + '\n' + examples['status_' + message.statusCode.toString().split('.')[1]].exArray.map((str) => { return str.ex }).join('\n') + '\nNote: ' + examples['status_' + message.statusCode.toString().split('.')[1]].note + '\n' + textContent(helpCenterLink));
        }
    } else {
        messageBody = <>{streaming ? messageText : replaceTextWithReferenceLinks(messageText)}{message.stopText !== undefined ? <div style={{ color: 'var(--soft-red)' }}>{message.stopText}</div> : ''}</>;
        messageToCopy = textContent(messageText);
        if (props.ind === 0) {
            messageToCopy = messageToCopy.concat('\n' + examples['welcome'].exArray.map((str) => { return str.ex }).join('\n'));
        }
    }

    return (
        <span className='message' ref={ref} key={'message ' + props.ind}>
            {isMobile
                ? null
                : message.user === 'VarChat'
                    ? <img src={robotIcon} alt='varChat' className='icon'></img>
                    : <img src={userIcon} alt='user' className='icon'></img>}
            <span name='message-text' className={message.user.toLowerCase()}>
                <span name='message-header'>
                    <span>
                        {!isMobile
                            ? null
                            : message.user === 'VarChat'
                                ? <img src={robotIcon} alt='varChat' className='icon' style={{ marginRight: '8px' }}></img>
                                : <img src={userIcon} alt='user' className='icon' style={{ marginRight: '8px' }}></img>}
                        <span style={{ display: isMobile ? 'block' : 'flex' }}>
                            <p name='name'>{message.user}</p>
                            <p name='time'>{message.time}</p>
                        </span>
                    </span>
                    {/* TODO CHECK WHY IS FAILING THE COPYICON */}
                    <span style={{ alignItems: 'center', marginRight: '-8px' }}>
                        {streaming && props.ind === messages.length - 1
                            ? null
                            :
                            <>
                                <CopyIcon ind={props.ind} messageToCopy={messageToCopy} type='copyText' />
                                {message.evaluate ? <>
                                    <CopyIcon ind={props.ind} type='copyLink' />
                                    {getStatusConfig(message.statusCode).type !== 'error'
                                        ? language !== 'EN' && logToken !== ''
                                            ? <MessageFlagIcons ind={props.ind} />
                                            : <button className='secondary-button small' name='translation-btn' onClick={clickNeedTranslation}>{!isMobile ? 'NEED A TRANSLATION?' : <svg name='translate-svg' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" id="language"><path d="M4 25a1 1 0 0 1-1-1V5a2.002 2.002 0 0 1 2-2h18a2.002 2.002 0 0 1 2 2v14a2.002 2.002 0 0 1-2 2H8.414l-3.707 3.707A.999.999 0 0 1 4 25zM23 4.998 5 5v16.586l2.293-2.293A1 1 0 0 1 8 19h15zM44 45a.999.999 0 0 1-.707-.293L39.586 41H25a2.002 2.002 0 0 1-2-2V25a2.002 2.002 0 0 1 2-2h18a2.002 2.002 0 0 1 2 2v19a1 1 0 0 1-1 1zM25 25v14h15a1 1 0 0 1 .707.293L43 41.586V25z"></path><path d="M10.998 17a1 1 0 0 1-.14-1.99 6.125 6.125 0 0 0 3.435-1.717A6.057 6.057 0 0 0 15.73 11H11a1 1 0 0 1 0-2h6a1 1 0 0 1 .99 1.141 8.07 8.07 0 0 1-6.848 6.849 1.008 1.008 0 0 1-.143.01Z"></path><path d="M14 11a1 1 0 0 1-1-1V8a1 1 0 0 1 2 0v2a1 1 0 0 1-1 1zm4.001 6q-.039 0-.08-.003a7.522 7.522 0 0 1-5.764-3.459 1 1 0 0 1 1.686-1.076 5.516 5.516 0 0 0 4.235 2.54A1 1 0 0 1 18.001 17zm11.998 20a1 1 0 0 1-.893-1.447l4-8a1 1 0 0 1 1.788.894l-4 8A1 1 0 0 1 30 37z"></path><path d="M38.001 37a1 1 0 0 1-.895-.553l-4-8a1 1 0 0 1 1.788-.894l4 8A1 1 0 0 1 38.002 37Z"></path><path d="M37.483 33.967h-6.966a1 1 0 0 1 0-2h6.966a1 1 0 0 1 0 2zM10 27a1 1 0 0 1-.707-1.707l2-2a1 1 0 0 1 1.414 1.414l-2 2A.997.997 0 0 1 10 27z"></path><path d="M14 27a.997.997 0 0 1-.707-.293l-2-2a1 1 0 0 1 1.414-1.414l2 2A1 1 0 0 1 14 27Z"></path><path d="M20 35h-7a2.002 2.002 0 0 1-2-2v-9a1 1 0 0 1 2 0v9h7a1 1 0 0 1 0 2zm16-14a1 1 0 0 1-.707-1.707l2-2a1 1 0 0 1 1.414 1.414l-2 2A.997.997 0 0 1 36 21z"></path><path d="M36 21a.997.997 0 0 1-.707-.293l-2-2a1 1 0 0 1 1.414-1.414l2 2A1 1 0 0 1 36 21Z"></path><path d="M36 21a1 1 0 0 1-1-1v-9h-7a1 1 0 0 1 0-2h7a2.002 2.002 0 0 1 2 2v9a1 1 0 0 1-1 1Z"></path></svg>}</button> : null}
                                </>
                                    : null
                                }
                            </>
                        }
                    </span>
                </span>
                <div style={{ whiteSpace: 'pre-line', lineBreak: (message.linebreak !== undefined && message.linebreak) ? 'anywhere' : 'auto' }}>
                    {/* message body cambia in base alla lingua */}
                    <div style={{ direction: message.language === 'AR' ? 'rtl' : '' }}>
                        {message.type !== 'unlogged-info' ? messageBody : <p>Want to know more about this variant? <a href="" className="message-link" onClick={clickedUnloggedUserLoginLink}>Log in</a> and continue the conversation with VarChat</p>}
                    </div>
                    {props.ind === 0
                        ? <WelcomeMessage />
                        : (
                            <>{hasntDiscoverMoreConditions()
                                ? null
                                : streaming
                                    ? null
                                    : <div className="message-section" onClick={onAskMoreClick} style={{ border: "thin solid var(--green)" }}>
                                        <div name="ref-title">
                                            <span>
                                                <p>Discover more about {previousMessage?.text}</p>
                                            </span>
                                            <span className="readMore">
                                                <p><img src={arrow} alt="arrow" /></p>
                                            </span>
                                        </div>
                                    </div>
                            }</>
                        )
                    }
                    {message.citCount && message.citCount !== 0
                        ? <div className='message-section'>
                            <div name='ref-title' onClick={() => setExpandRef(!expandRef)}>
                                <span><p>REFERENCES&nbsp;</p><p> ({message.citCount} FOUND)&nbsp;</p></span>
                                <ReadMore expand={expandRef} setExpand={setExpandRef} messageShow={"READ MORE"} messageHide={"READ LESS"} />
                            </div>
                            {createRefTable()}
                        </div>
                        : null
                    }
                    {message.rcvCitCount && message.rcvCitCount !== 0
                        ? <div className='message-section'>
                            <div name='ref-title' onClick={() => setExpand(!expand)}>
                                <span><p>CLINVAR&nbsp;</p><p>({message.rcvCitCount} RCV{message.rcvCitCount > 1 ? 's' : ''} FOUND)&nbsp;</p></span>
                                <ReadMore expand={expand} setExpand={setExpand} messageShow={"READ MORE"} messageHide={"READ LESS"} />
                            </div>
                            {createRcvRefTable()}
                        </div>
                        : null
                    }

                </div>
                {message.evaluate && getStatusConfig(message.statusCode).type !== 'error' ? <Rating ind={props.ind} scope={message.type === 'answer' ? 'answer' : 'query'} /> : null}

            </span>
        </span>
    );
})

export default Message;
